import {Component, Input, OnChanges, ChangeDetectionStrategy} from '@angular/core';
import {finalize, switchMap} from 'rxjs/operators';
import {AuthService} from '../../_services/auth.service';
import {RoleService} from '../../_services/role.service';
import {TokenStorageService} from '../../_services/token-storage.service';
import {ReadOnlyService} from '../../_services/read-only.service';

export const ROLE_LABELS: Record<string, string> = {
  ROLE_SUPER_ADMIN: 'Super admin',
  ROLE_ADMIN: 'Admin',
  ROLE_MONITOR: 'Moniteur',
  ROLE_READER: 'Lecteur'
};

/**
 * Gestion des rôles d'un compte du personnel (moniteur, secrétariat...) retrouvé par son email.
 * Le rôle élève (ROLE_USER) est réservé aux comptes élèves et n'est pas proposé ici.
 * Modifiable par un admin ou un super admin ; seul un super admin gère le rôle super admin.
 */
@Component({
    selector: 'app-user-roles',
    templateUrl: './user-roles.component.html',
    styleUrls: ['./user-roles.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserRolesComponent implements OnChanges {

  @Input() iEmail?: string;

  readonly labels = ROLE_LABELS;

  userId: string | null = null;
  userRoles: string[] = [];
  loaded = false;
  noAccount = false;
  loadingRole: string | null = null;
  errorMessage = '';

  constructor(private authService: AuthService, private roleService: RoleService,
              private tokenStorage: TokenStorageService, private readOnlyService: ReadOnlyService) {
  }

  ngOnChanges(): void {
    this.reset();
    if (!this.iEmail) {
      return;
    }
    this.authService.getUserByEmail(this.iEmail).pipe(
      switchMap((user: any) => {
        if (!user?.id) {
          throw new Error('no-account');
        }
        this.userId = String(user.id);
        return this.roleService.getUserRoles(this.userId);
      }),
      finalize(() => this.loaded = true)
    ).subscribe(
      (roles: string[]) => this.userRoles = roles ?? [],
      () => this.noAccount = true
    );
  }

  get canEdit(): boolean {
    return !this.readOnlyService.isReadOnly() && (this.hasCurrentRole('ROLE_ADMIN') || this.hasCurrentRole('ROLE_SUPER_ADMIN'));
  }

  /** Rôles que l'utilisateur connecté peut attribuer et que le compte n'a pas encore. */
  get availableRoles(): string[] {
    return Object.keys(ROLE_LABELS)
      .filter(role => this.canManage(role) && !this.userRoles.includes(role));
  }

  canManage(role: string): boolean {
    return this.canEdit && (role !== 'ROLE_SUPER_ADMIN' || this.hasCurrentRole('ROLE_SUPER_ADMIN'));
  }

  label(role: string): string {
    return ROLE_LABELS[role] || role;
  }

  addRole(role: string): void {
    this.update(role, this.roleService.addRole.bind(this.roleService));
  }

  removeRole(role: string): void {
    this.update(role, this.roleService.removeRole.bind(this.roleService));
  }

  private update(role: string, call: (userId: string, role: string) => any): void {
    if (!this.userId || this.loadingRole) {
      return;
    }
    const userId = this.userId;
    this.loadingRole = role;
    this.errorMessage = '';
    call(userId, role).pipe(
      switchMap(() => this.roleService.getUserRoles(userId)),
      finalize(() => this.loadingRole = null)
    ).subscribe(
      (roles: string[]) => this.userRoles = roles ?? [],
      (err: any) => {
        this.errorMessage = typeof err.error === 'string' ? err.error : 'La modification du rôle a échoué';
        this.roleService.getUserRoles(userId).subscribe((roles: string[]) => this.userRoles = roles ?? []);
      }
    );
  }

  private hasCurrentRole(role: string): boolean {
    return (this.tokenStorage.getUser()?.roles ?? []).includes(role);
  }

  private reset(): void {
    this.userId = null;
    this.userRoles = [];
    this.loaded = false;
    this.noAccount = false;
    this.errorMessage = '';
  }
}
